import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import {
  AdminPageHeader,
  AdminStatCard,
  AdminTable,
  AdminEmptyState,
  type AdminTableColumn,
} from '../../components/admin';
import { ConfirmDialog } from '../../components/ConfirmDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PaymentStats {
  activeSubscriptions: {
    premium: number;
    unlimited: number;
    total: number;
  };
  mrr: number;
  estimatedAnnualRevenue: number;
  currency: string;
  churn: {
    cancelled: number;
    total: number;
    rate: number;
  };
}

interface SubscriptionRow {
  id: string;
  userId: string;
  stripeSubscriptionId: string | null;
  plan: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  updatedAt: string;
  userEmail: string;
  userName: string | null;
}

// api.get() extracts .data from ApiResponse, so we receive the rows directly.
// Pagination info is lost through the wrapper - use array length as fallback.
type SubscriptionList = SubscriptionRow[];

// ---------------------------------------------------------------------------
// Labels & colors
// ---------------------------------------------------------------------------

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  premium: 'bg-blue-100 text-blue-700',
  unlimited: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  past_due: 'bg-amber-100 text-amber-700',
  trialing: 'bg-blue-100 text-blue-700',
  incomplete: 'bg-gray-100 text-gray-700',
  incomplete_expired: 'bg-gray-100 text-gray-700',
  unpaid: 'bg-red-100 text-red-700',
  paused: 'bg-gray-100 text-gray-700',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminApi, isSuperAdmin } = useAdmin();
  const { t, locale } = useTranslation();
  const intlLocale = locale === 'en' ? 'en-US' : 'it-IT';

  const PLAN_LABELS: Record<string, string> = {
    free: t('admin.common.planFree'),
    premium: t('admin.common.planPremium'),
    unlimited: t('admin.common.planUnlimited'),
  };

  const STATUS_LABELS: Record<string, string> = {
    active: t('admin.common.subStatusActive'),
    cancelled: t('admin.common.subStatusCancelled'),
    past_due: t('admin.common.subStatusPastDue'),
    trialing: t('admin.common.subStatusTrialing'),
    incomplete: t('admin.common.subStatusIncomplete'),
    incomplete_expired: t('admin.common.subStatusIncompleteExpired'),
    unpaid: t('admin.common.subStatusUnpaid'),
    paused: t('admin.common.subStatusPaused'),
  };

  function formatCurrency(value: number): string {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(intlLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  // Stats
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Subscriptions
  const [subsData, setSubsData] = useState<SubscriptionList | null>(null);
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsError, setSubsError] = useState<string | null>(null);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState<SubscriptionRow | null>(null);
  const [refunding, setRefunding] = useState(false);

  // Filters from URL
  const planFilter = searchParams.get('plan') || '';
  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        if (key !== 'page') {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // ---------------------------------------------------------------------------
  // Fetch stats
  // ---------------------------------------------------------------------------
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const result = await adminApi.get<PaymentStats>(
        '/payments/stats',
      );
      setStats(result);
    } catch (err) {
      console.error('Errore nel caricamento statistiche pagamenti:', err);
      setStatsError(t('admin.payments.statsLoadError'));
    } finally {
      setStatsLoading(false);
    }
  }, [adminApi, t]);

  // ---------------------------------------------------------------------------
  // Fetch subscriptions
  // ---------------------------------------------------------------------------
  const fetchSubscriptions = useCallback(async () => {
    setSubsLoading(true);
    setSubsError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (planFilter) params.set('plan', planFilter);
      if (statusFilter) params.set('status', statusFilter);

      const result = await adminApi.get<SubscriptionRow[]>(
        `/payments/subscriptions?${params.toString()}`,
      );
      setSubsData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Errore nel caricamento abbonamenti:', err);
      setSubsError(t('admin.payments.subsLoadError'));
    } finally {
      setSubsLoading(false);
    }
  }, [adminApi, page, planFilter, statusFilter, t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // ---------------------------------------------------------------------------
  // Refund handler
  // ---------------------------------------------------------------------------
  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await adminApi.post(`/payments/subscriptions/${refundTarget.id}/refund`);
      setRefundTarget(null);
      await fetchSubscriptions();
      await fetchStats();
    } catch (err) {
      console.error('Errore durante il rimborso:', err);
      alert(t('admin.payments.refundError'));
    } finally {
      setRefunding(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------
  const columns: AdminTableColumn<SubscriptionRow>[] = [
    {
      key: 'userEmail',
      label: t('admin.payments.colUser'),
      render: (row) => (
        <div>
          <span className="font-medium">{row.userEmail}</span>
          {row.userName && (
            <span className="ml-2 text-text-secondary text-xs">({row.userName})</span>
          )}
        </div>
      ),
    },
    {
      key: 'plan',
      label: t('admin.payments.colPlan'),
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PLAN_COLORS[row.plan] || 'bg-gray-100 text-gray-700'}`}
        >
          {PLAN_LABELS[row.plan] || row.plan}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('admin.payments.colStatus'),
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-700'}`}
        >
          {STATUS_LABELS[row.status] || row.status}
        </span>
      ),
    },
    {
      key: 'currentPeriodStart',
      label: t('admin.payments.colPeriodStart'),
      render: (row) => formatDate(row.currentPeriodStart),
    },
    {
      key: 'currentPeriodEnd',
      label: t('admin.payments.colPeriodEnd'),
      render: (row) => formatDate(row.currentPeriodEnd),
    },
    {
      key: 'actions',
      label: t('admin.payments.colActions'),
      render: (row) => (
        <div className="flex items-center gap-2">
          {isSuperAdmin && row.stripeSubscriptionId && row.status === 'active' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setRefundTarget(row);
              }}
              className="text-sm font-medium text-red-600 hover:text-red-700"
            >
              {t('admin.payments.refund')}
            </button>
          )}
          {!isSuperAdmin && (
            <span className="text-xs text-text-secondary">-</span>
          )}
          {isSuperAdmin && (!row.stripeSubscriptionId || row.status !== 'active') && (
            <span className="text-xs text-text-secondary">-</span>
          )}
        </div>
      ),
    },
  ];

  const totalPages = 1; // Pagination info lost through api wrapper - show all results

  return (
    <div>
      <AdminPageHeader
        title={t('admin.payments.title')}
        subtitle={
          stats
            ? t('admin.payments.activeCount').replace('{count}', String(stats.activeSubscriptions.total))
            : undefined
        }
      />

      {/* Stats row */}
      {statsError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {statsError}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-lg border border-border bg-surface p-5"
            >
              <div className="h-4 w-20 rounded bg-gray-200 mb-2" />
              <div className="h-7 w-28 rounded bg-gray-200" />
            </div>
          ))
        ) : stats ? (
          <>
            <AdminStatCard
              label={t('admin.payments.mrr')}
              value={formatCurrency(stats.mrr)}
            />
            <AdminStatCard
              label={t('admin.payments.activeSubscriptions')}
              value={stats.activeSubscriptions.total}
            />
            <AdminStatCard
              label={t('admin.payments.churnRate')}
              value={`${(stats.churn.rate * 100).toFixed(2)}%`}
            />
            <AdminStatCard
              label={t('admin.payments.annualRevenue')}
              value={formatCurrency(stats.estimatedAnnualRevenue)}
            />
          </>
        ) : null}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          value={planFilter}
          onChange={(e) => setParam('plan', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('admin.payments.filterAllPlans')}</option>
          <option value="free">{t('admin.common.planFree')}</option>
          <option value="premium">{t('admin.common.planPremium')}</option>
          <option value="unlimited">{t('admin.common.planUnlimited')}</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setParam('status', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('admin.payments.filterAllStatuses')}</option>
          <option value="active">{t('admin.payments.filterStatusActive')}</option>
          <option value="cancelled">{t('admin.payments.filterStatusCancelled')}</option>
          <option value="past_due">{t('admin.payments.filterStatusPastDue')}</option>
          <option value="trialing">{t('admin.payments.filterStatusTrialing')}</option>
          <option value="unpaid">{t('admin.payments.filterStatusUnpaid')}</option>
          <option value="paused">{t('admin.payments.filterStatusPaused')}</option>
        </select>
      </div>

      {/* Error state */}
      {subsError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {subsError}
        </div>
      )}

      {/* Table */}
      {!subsLoading && subsData && subsData.length === 0 ? (
        <AdminEmptyState
          title={t('admin.payments.emptyTitle')}
          description={t('admin.payments.emptyDescription')}
        />
      ) : (
        <AdminTable
          columns={columns}
          data={subsData ?? []}
          loading={subsLoading}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            {t('admin.common.pageOf').replace('{page}', String(page)).replace('{total}', String(totalPages))} ({t('admin.common.resultsCount').replace('{count}', String(subsData?.length ?? 0))})
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.previous')}
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setParam('page', String(page + 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.next')}
            </button>
          </div>
        </div>
      )}

      {/* Refund confirmation dialog */}
      <ConfirmDialog
        open={refundTarget !== null}
        title={t('admin.payments.refundConfirmTitle')}
        message={
          refundTarget ? (
            <>
              {t('admin.payments.refundConfirmPrefix')}{' '}
              <strong>{refundTarget.userEmail}</strong> ({t('admin.payments.refundConfirmPlanWord')}{' '}
              {PLAN_LABELS[refundTarget.plan] || refundTarget.plan})?{' '}
              {t('admin.payments.refundConfirmSuffix')}
            </>
          ) : ''
        }
        confirmLabel={t('admin.payments.refund')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        isLoading={refunding}
        onConfirm={handleRefund}
        onCancel={() => setRefundTarget(null)}
      />
    </div>
  );
}
